import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Package, Upload, SignOut, Download, Key, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MainAdminDashboard() {
  const { user, logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showUploadProducts, setShowUploadProducts] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeCredentials, setShowChangeCredentials] = useState(false);
  const [showResetSubAdmin, setShowResetSubAdmin] = useState(false);
  const [resetSubAdminClientId, setResetSubAdminClientId] = useState('');
  const [resetSubAdminClientName, setResetSubAdminClientName] = useState('');
  const [resetSubAdminNewPassword, setResetSubAdminNewPassword] = useState('');

  // Change Credentials Form
  const [ccCurrentEmail, setCcCurrentEmail] = useState('');
  const [ccCurrentPassword, setCcCurrentPassword] = useState('');
  const [ccNewEmail, setCcNewEmail] = useState('');
  const [ccNewPassword, setCcNewPassword] = useState('');
  const [ccConfirmPassword, setCcConfirmPassword] = useState('');

  // Change Password Form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Create Client Form
  const [clientName, setClientName] = useState('');
  const [subAdminEmail, setSubAdminEmail] = useState('');
  const [subAdminPassword, setSubAdminPassword] = useState('');

  // Upload Products
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadClientId, setUploadClientId] = useState('');

  useEffect(() => {
    fetchClients();
    fetchProducts();
  }, []);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchProducts = async (clientId = '') => {
    try {
      const url = clientId ? `${API_URL}/api/products?client_id=${clientId}` : `${API_URL}/api/products`;
      const { data } = await axios.get(url, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/clients`,
        {
          name: clientName,
          sub_admin_email: subAdminEmail,
          sub_admin_password: subAdminPassword
        },
        { withCredentials: true }
      );
      toast.success('Client created successfully');
      setShowCreateClient(false);
      setClientName('');
      setSubAdminEmail('');
      setSubAdminPassword('');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadProducts = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadClientId) {
      toast.error('Please select a file and client');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/products/upload?client_id=${uploadClientId}`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success(data.message);
      setShowUploadProducts(false);
      setUploadFile(null);
      setUploadClientId('');
      fetchProducts();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload products');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterByClient = (clientId) => {
    setSelectedClient(clientId);
    // "all" means show all clients (pass empty string to API)
    fetchProducts(clientId === 'all' ? '' : clientId);
  };

  const downloadQRCode = (qrCode, variableNumber) => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `QR_${variableNumber}.png`;
    link.click();
  };

  const handleDeleteProduct = async (variableNumber) => {
    try {
      await axios.delete(
        `${API_URL}/api/products/${encodeURIComponent(variableNumber)}`,
        { withCredentials: true }
      );
      toast.success(`Product ${variableNumber} deleted`);
      fetchProducts(selectedClient && selectedClient !== 'all' ? selectedClient : '');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const openResetSubAdmin = (client) => {
    setResetSubAdminClientId(client._id);
    setResetSubAdminClientName(client.name);
    setResetSubAdminNewPassword('');
    setShowResetSubAdmin(true);
  };

  const handleChangeCredentials = async (e) => {
    e.preventDefault();
    if (ccNewPassword !== ccConfirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (ccNewPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-credentials`,
        {
          current_email: ccCurrentEmail,
          current_password: ccCurrentPassword,
          new_email: ccNewEmail,
          new_password: ccNewPassword
        },
        { withCredentials: true }
      );
      toast.success('Credentials updated. Please log in again.');
      setShowChangeCredentials(false);
      setCcCurrentEmail('');
      setCcCurrentPassword('');
      setCcNewEmail('');
      setCcNewPassword('');
      setCcConfirmPassword('');
      // Force logout so user re-authenticates with new credentials
      setTimeout(() => logout(), 800);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubAdminPassword = async (e) => {
    e.preventDefault();
    if (resetSubAdminNewPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/reset-sub-admin-password`,
        { client_id: resetSubAdminClientId, new_password: resetSubAdminNewPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${data.sub_admin_email}`);
      setShowResetSubAdmin(false);
      setResetSubAdminNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset sub-admin password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Package size={24} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900">Product Vault</h1>
              <p className="text-sm text-slate-600">Main Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.email}</span>
            <Dialog open={showChangeCredentials} onOpenChange={setShowChangeCredentials}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="change-credentials-button"
                  className="hover:bg-slate-100"
                >
                  <Key size={20} className="mr-2" />
                  Change Login ID & Password
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Login Credentials</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleChangeCredentials} className="space-y-4">
                  <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                    Enter your current login ID (email) and password to verify, then set your new credentials. You will be logged out after the change.
                  </div>
                  <div>
                    <Label htmlFor="ccCurrentEmail">Current Login ID (Email)</Label>
                    <Input
                      id="ccCurrentEmail"
                      type="email"
                      value={ccCurrentEmail}
                      onChange={(e) => setCcCurrentEmail(e.target.value)}
                      required
                      data-testid="cc-current-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ccCurrentPassword">Current Password</Label>
                    <Input
                      id="ccCurrentPassword"
                      type="password"
                      value={ccCurrentPassword}
                      onChange={(e) => setCcCurrentPassword(e.target.value)}
                      required
                      data-testid="cc-current-password-input"
                    />
                  </div>
                  <div className="border-t border-slate-200 pt-4">
                    <Label htmlFor="ccNewEmail">New Login ID (Email)</Label>
                    <Input
                      id="ccNewEmail"
                      type="email"
                      value={ccNewEmail}
                      onChange={(e) => setCcNewEmail(e.target.value)}
                      required
                      data-testid="cc-new-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ccNewPassword">New Password</Label>
                    <Input
                      id="ccNewPassword"
                      type="password"
                      value={ccNewPassword}
                      onChange={(e) => setCcNewPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="cc-new-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ccConfirmPassword">Confirm New Password</Label>
                    <Input
                      id="ccConfirmPassword"
                      type="password"
                      value={ccConfirmPassword}
                      onChange={(e) => setCcConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="cc-confirm-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="cc-submit-button">
                    {loading ? 'Updating...' : 'Update Credentials'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
              className="hover:bg-slate-100"
            >
              <SignOut size={20} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-sm transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Clients</p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-1">{clients.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users size={28} weight="duotone" className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-sm transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Products</p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-1">{products.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Package size={28} weight="duotone" className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <Dialog open={showCreateClient} onOpenChange={setShowCreateClient}>
              <DialogTrigger asChild>
                <Button className="w-full bg-slate-900 hover:bg-slate-800 active:scale-95 transition-transform duration-200" data-testid="create-client-button">
                  <Users size={20} className="mr-2" weight="bold" />
                  Create Client
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Client</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-4">
                  <div>
                    <Label htmlFor="clientName">Client Name</Label>
                    <Input
                      id="clientName"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Acme Corporation"
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subAdminEmail">Sub-Admin Email</Label>
                    <Input
                      id="subAdminEmail"
                      type="email"
                      value={subAdminEmail}
                      onChange={(e) => setSubAdminEmail(e.target.value)}
                      placeholder="admin@acme.com"
                      required
                      data-testid="sub-admin-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subAdminPassword">Sub-Admin Password</Label>
                    <Input
                      id="subAdminPassword"
                      type="password"
                      value={subAdminPassword}
                      onChange={(e) => setSubAdminPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      data-testid="sub-admin-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="create-client-submit-button">
                    {loading ? 'Creating...' : 'Create Client'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showUploadProducts} onOpenChange={setShowUploadProducts}>
              <DialogTrigger asChild>
                <Button className="w-full mt-3 bg-blue-600 hover:bg-blue-700 active:scale-95 transition-transform duration-200" data-testid="upload-products-button">
                  <Upload size={20} className="mr-2" weight="bold" />
                  Upload Products
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bulk Upload Products</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUploadProducts} className="space-y-4">
                  <div>
                    <Label htmlFor="uploadClient">Select Client</Label>
                    <Select value={uploadClientId} onValueChange={setUploadClientId} required>
                      <SelectTrigger data-testid="upload-client-select">
                        <SelectValue placeholder="Choose a client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client._id} value={client._id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="uploadFile">Excel File</Label>
                    <Input
                      id="uploadFile"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      required
                      data-testid="upload-file-input"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Required columns: product_name, variable_number, description
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="upload-products-submit-button">
                    {loading ? 'Uploading...' : 'Upload Products'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="products" data-testid="products-tab">Products</TabsTrigger>
            <TabsTrigger value="clients" data-testid="clients-tab">Clients</TabsTrigger>
          </TabsList>

          <TabsContent value="products" data-testid="products-tab-content">
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold tracking-tight text-slate-900">Products</h2>
                  <Select value={selectedClient} onValueChange={handleFilterByClient}>
                    <SelectTrigger className="w-64" data-testid="filter-client-select">
                      <SelectValue placeholder="Filter by client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client._id} value={client._id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Variable Number</TableHead>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Client ID</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          No products found
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((product, index) => (
                        <TableRow key={index} className="hover:bg-slate-50 transition-colors duration-150">
                          <TableCell className="font-mono font-medium text-slate-900">
                            {product.variable_number}
                          </TableCell>
                          <TableCell>{product.product_name}</TableCell>
                          <TableCell className="max-w-xs truncate">{product.description}</TableCell>
                          <TableCell className="font-mono text-sm text-slate-600">{product.client_id}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => downloadQRCode(product.qr_code, product.variable_number)}
                                data-testid={`download-qr-${product.variable_number}`}
                                className="hover:bg-slate-100"
                                title="Download QR"
                              >
                                <Download size={18} />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`delete-product-${product.variable_number}`}
                                    className="hover:bg-red-50 text-red-600 hover:text-red-700"
                                    title="Delete product"
                                  >
                                    <Trash size={18} />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this product?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Number <span className="font-mono font-semibold">{product.variable_number}</span> will be permanently removed from the server along with any authentication record. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel data-testid={`cancel-delete-${product.variable_number}`}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteProduct(product.variable_number)}
                                      className="bg-red-600 hover:bg-red-700"
                                      data-testid={`confirm-delete-${product.variable_number}`}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="clients" data-testid="clients-tab-content">
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold tracking-tight text-slate-900">Clients</h2>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client Name</TableHead>
                      <TableHead>Client ID</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          No clients found
                        </TableCell>
                      </TableRow>
                    ) : (
                      clients.map((client) => (
                        <TableRow key={client._id} className="hover:bg-slate-50 transition-colors duration-150">
                          <TableCell className="font-medium text-slate-900">{client.name}</TableCell>
                          <TableCell className="font-mono text-sm text-slate-600">{client._id}</TableCell>
                          <TableCell>{client.product_count || 0}</TableCell>
                          <TableCell className="text-slate-600">
                            {new Date(client.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openResetSubAdmin(client)}
                              data-testid={`reset-sub-admin-${client._id}`}
                            >
                              <Key size={16} className="mr-2" />
                              Reset Sub-Admin Password
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Reset Sub-Admin Password Dialog */}
      <Dialog open={showResetSubAdmin} onOpenChange={setShowResetSubAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Sub-Admin Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetSubAdminPassword} className="space-y-4">
            <div>
              <Label>Client</Label>
              <p className="text-sm text-slate-900 font-medium mt-1">{resetSubAdminClientName}</p>
            </div>
            <div>
              <Label htmlFor="resetNewPassword">New Password</Label>
              <Input
                id="resetNewPassword"
                type="password"
                value={resetSubAdminNewPassword}
                onChange={(e) => setResetSubAdminNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                data-testid="reset-sub-admin-password-input"
              />
              <p className="text-xs text-slate-500 mt-2">
                Share this new password with the client via a secure channel.
              </p>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="reset-sub-admin-submit-button">
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
